import { View, Text, TextInput, StyleSheet } from "react-native";
import React, { useState, useEffect, useMemo } from "react";
import SerieRowView from "../Serie/SerieRowView";
import TextareaAutosize from "react-textarea-autosize";

import GlobalStyles from "../../Resources/Styles";
import Colors from "../../Resources/Color";
import Fonts from "../../Resources/Fonts";
import { observer } from "mobx-react";
import { useScreenDimensions, useDevice } from "../_layout";
import { reaction } from "mobx";
import { useTranslation } from "react-i18next";
import SerieRowHeader from "../Serie/SerieRowHeader";

const ExerciseDescriptionView = ({ exercise, showSetPriority }) => {
  const { t } = useTranslation();
  const { isMobile } = useDevice();

  function formatNumber(num) {
    if (Math.floor(num) === num) return num.toString();
    return parseFloat(num.toFixed(1)).toString();
  }
  const { screenSize } = useScreenDimensions();

  const StatisticsRowView = observer(() => {
    const e1rm = formatNumber(exercise.getMaxErm);
    const tonnage = formatNumber(exercise.getTonnage);
    const stressIndex = formatNumber(exercise.getStressIndex);
    const rirOrRpe = exercise.getRirOrRpe;

    const StatItem = ({ label, value, unit }) => (
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          marginLeft: 12,
        }}
      >
        <Text
          style={{
            fontSize: 12,
            fontWeight: "500",
            color: Colors.grayColor,
            marginRight: 4,
          }}
        >
          {label}
        </Text>
        <Text
          style={{
            fontSize: 12,
            fontWeight: "500",
            color: Colors.darkgrayColor,
          }}
        >
          {value} {unit}
        </Text>
      </View>
    );

    return (
      <View
        style={[
          GlobalStyles.HStack,
          {
            width: "100%",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            paddingHorizontal: 12,
          },
        ]}
      >
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          <Text
            style={{
              fontSize: 12,
              fontWeight: "500",
              color: Colors.grayColor,
            }}
          >
            {t("stats-string")}
          </Text>
        </View>

        <View style={{ flexDirection: "row", alignItems: "center" }}>
          <StatItem
            label="E1RM"
            value={e1rm}
            unit={exercise.getExerciseUnitString}
          />
          <StatItem
            label={t("tonnage-string")}
            value={tonnage}
            unit={exercise.getExerciseUnitString}
          />
          {!rirOrRpe && <StatItem label="S.I." value={stressIndex} unit="" />}
        </View>
      </View>
    );
  });

  const TextNotes = () => {
    const [string, setString] = useState(exercise.notes ? exercise.notes : "");
    const [refresh, setRefresh] = useState(false);

    useEffect(() => {
      setString(exercise.notes ? exercise.notes : "");
    }, [refresh]);

    useEffect(() => {
      const dispose = reaction(
        () => ({
          updateSets: exercise.updateSets,
        }),
        () => {
          setRefresh((prev) => !prev);
        }
      );

      return () => dispose();
    }, []);

    const handleChange = (e) => {
      const text = e.target.value;
      setString(text);
      exercise.setProperty("notes", text);
    };

    return (
      <View
        style={[
          GlobalStyles.HStack,
          {
            marginTop: 2,
            // width: "100%",
            flexDirection: "row",
            alignItems: "flex-start",
            flex: 1,
            paddingHorizontal: 12,
          },
        ]}
      >
        <View
          style={{
            width: 3,
            alignSelf: "stretch",
            backgroundColor: Colors.grayColor,
            borderRadius: 4,
            marginRight: 4,
            marginTop: 1,
            marginBottom: 1,
          }}
        />

        <View
          style={{
            flex: 1,
            overflow: "hidden",
            borderTopRightRadius: 10,
            borderBottomRightRadius: 10,
          }}
        >
          <TextareaAutosize
            minRows={1}
            maxRows={8}
            placeholder={t("notesDots-string")}
            value={string}
            onChange={handleChange}
            autoCorrect="off"
            spellCheck="false"
            style={{
              width: "100%",
              resize: "none",
              fontFamily:
                '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
              border: "none",
              outline: "none",
              fontSize: isMobile ? 12 : 12,
              fontWeight: "500",
              color: Colors.darkgrayColor,
              backgroundColor: "transparent",
              padding: 0,
              margin: 0,
              textAlign: "left",
              lineHeight: "normal",
            }}
            className="exercise-notes-textarea"
            maxLength={900}
          />
        </View>
      </View>
    );
  };

  const ConditionalStatsAndNotes = observer(() => {
    return (
      <View style={{ flex: 1, gap: 8, marginTop: 8 }}>
        {exercise.hasSeriesWithData && <StatisticsRowView />}
        <TextNotes />
      </View>
    );
  });

  const SeriesList = observer(() => {
    // Create a stable reference based on series IDs only
    const seriesIds =
      exercise.getSortedSeries?.map((s) => s.id).join(",") || "";

    // Memoize the series list so it only changes when series are added/removed
    const series = useMemo(() => {
      return exercise.getSortedSeries || [];
    }, [seriesIds]);

    return (
      <>
        {series.map((serie) => (
          <SerieRowView
            key={serie.id}
            serie={serie}
            showSetPriority={showSetPriority}
          />
        ))}
      </>
    );
  });

  return (
    <View
      style={{
        width: "100%",
        justifyContent: "center",
        marginTop: 8,
        marginBottom: 10,
      }}
    >
      <SerieRowHeader exercise={exercise} showSetPriority={showSetPriority} />
      <SeriesList />
      <ConditionalStatsAndNotes />
    </View>
  );
};

const styles = StyleSheet.create({
  background: {
    height: 60,
    // backgroundColor: "white", // Assuming you have this color defined elsewhere
    borderRadius: 10,
    paddingHorizontal: 10,
    justifyContent: "center",
    shadowLayer: {
      backgroundColor: "rgba(0,0,0,0.1)", // Adjust this for shadow color
      position: "absolute",
      top: 3,
      bottom: 3,
      left: 3,
      right: 3,
      borderRadius: 10,
    },
    shadowColor: Colors.bubbleColor, // Assuming you have this color defined elsewhere
    shadowOffset: { width: 2, height: 2 },
    shadowOpacity: 0.5,
    shadowRadius: 1,
    elevation: 1,
    marginHorizontal: 10, // Corresponds to .padding(.horizontal) in SwiftUI
  },
  title: {
    fontSize: 14,
    fontWeight: "500",
  },
});
export default ExerciseDescriptionView;
